import type { UserManagementApi } from '@liks/backend-svc-api/esm/apis/UserManagementApi';
import type { KeycloakClientRole } from '../auth/clientRoles';
import type { CurrentUserProfile } from '../types/user';
import { isUserNotFoundError, sleep } from './userServiceGuards';
import type { DirectoryUserWithId } from './userServiceTypes';

const PASSWORD_SETUP_EMAIL_ACTIONS = ['UPDATE_PASSWORD'] as const;

/**
 * Keycloak can lag briefly after create; wait until the user can be read
 * before follow-up calls like setting roles.
 */
export async function waitUntilUserReadable(
  getUser: (userId: string) => Promise<CurrentUserProfile>,
  userId: string,
  options?: { timeoutMs?: number; intervalMs?: number },
): Promise<CurrentUserProfile> {
  const timeoutMs = options?.timeoutMs ?? 10_000;
  const intervalMs = options?.intervalMs ?? 300;
  const deadline = Date.now() + timeoutMs;
  let lastError: unknown;

  while (Date.now() < deadline) {
    try {
      return await getUser(userId);
    } catch (error) {
      lastError = error;
      if (!isUserNotFoundError(error)) {
        throw error;
      }
      await sleep(intervalMs);
    }
  }

  if (lastError instanceof Error) {
    throw lastError;
  }
  throw new Error(`User "${userId}" was not readable after create`);
}

export async function waitUntilUserReadableByUsername(
  listUsers: () => Promise<DirectoryUserWithId[]>,
  username: string,
  options?: { timeoutMs?: number; intervalMs?: number },
): Promise<DirectoryUserWithId> {
  const timeoutMs = options?.timeoutMs ?? 10_000;
  const intervalMs = options?.intervalMs ?? 300;
  const deadline = Date.now() + timeoutMs;
  const normalized = username.trim().toLowerCase();

  while (Date.now() < deadline) {
    const users = await listUsers();
    const found = users.find(
      (user) => user.username?.trim().toLowerCase() === normalized,
    );
    if (found) {
      return found;
    }
    await sleep(intervalMs);
  }

  throw new Error(`User "${username}" was not readable after create`);
}

export async function sendPasswordSetupEmail(
  userApi: UserManagementApi,
  userId: string,
): Promise<void> {
  await userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceSendUserActionsEmail(
    {
      userId,
      requestBody: [...PASSWORD_SETUP_EMAIL_ACTIONS],
    },
  );
}

export async function sendPasswordSetupEmailWithRetry(
  userApi: UserManagementApi,
  userId: string,
  options?: { intervalMs?: number },
): Promise<void> {
  const intervalMs = options?.intervalMs ?? 300;

  try {
    await sendPasswordSetupEmail(userApi, userId);
    return;
  } catch (error) {
    if (!isUserNotFoundError(error)) {
      throw error;
    }
  }

  await sleep(intervalMs);
  await sendPasswordSetupEmail(userApi, userId);
}

export async function setUserRolesWithRetry(
  setUserRoles: (
    userId: string,
    roleNames: readonly KeycloakClientRole[],
  ) => Promise<void>,
  userId: string,
  roleNames: readonly KeycloakClientRole[],
  options?: { intervalMs?: number },
): Promise<void> {
  const intervalMs = options?.intervalMs ?? 300;

  try {
    await setUserRoles(userId, roleNames);
    return;
  } catch (error) {
    if (!isUserNotFoundError(error)) {
      throw error;
    }
  }

  await sleep(intervalMs);
  await setUserRoles(userId, roleNames);
}
